import { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import { MdCameraAlt, MdSave, MdLock, MdPerson, MdLink, MdContentCopy } from 'react-icons/md';

// In production, default to configured API URL or current origin; in development use localhost:5000.
const API_URL =
  process.env.NODE_ENV === 'production'
    ? (process.env.REACT_APP_API_URL || window.location.origin)
    : 'http://localhost:5000';

const Profile = () => {
  const { user } = useAuth();
  const [form, setForm]     = useState({ name: user?.name || '', phone: user?.phone || '' });
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '' });
  const [loading, setLoading]   = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(() => {
    if (!user?.avatar) return null;
    // If backend returns a full URL, use it as-is; otherwise prefix with API_URL.
    return user.avatar.startsWith('http') ? user.avatar : `${API_URL}${user.avatar}`;
  });
  const fileRef = useRef();

  const COLORS = ['#6366f1','#f59e0b','#10b981','#ef4444','#3b82f6'];
  const bgColor = COLORS[(user?.name?.charCodeAt(0) || 0) % COLORS.length];
  const getInitials = (name) => name?.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0,2);

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('avatar', file);
      const { data } = await api.post('/users/avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      // Update stored user so avatar shows everywhere immediately
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      stored.avatar = data.avatarUrl;
      localStorage.setItem('user', JSON.stringify(stored));
      toast.success('Profile photo updated!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
      setAvatarPreview(user?.avatar ? `${API_URL}${user.avatar}` : null);
    } finally { setUploading(false); }
  };

  const handleProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put('/users/profile', form);
      toast.success('Profile updated');
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
    finally { setLoading(false); }
  };

  const handlePassword = async (e) => {
    e.preventDefault();
    setPwLoading(true);
    try {
      await api.put('/auth/updatepassword', pwForm);
      toast.success('Password updated');
      setPwForm({ currentPassword: '', newPassword: '' });
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
    finally { setPwLoading(false); }
  };

  // ── Task Submission Link (shared, no token) ──
  const submitLinkUrl = `${window.location.origin}/submit`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(submitLinkUrl)
      .then(() => toast.success('Link copied!'))
      .catch(() => toast.error('Copy failed'));
  };

  return (
    <main className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">My Profile</h1>
          <p className="page-subtitle">Manage your account settings and photo</p>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'300px 1fr', gap: 24, maxWidth: 860 }}>

        {/* ── Photo Card ── */}
        <div className="card card-body" style={{ textAlign:'center', alignSelf:'start' }}>
          <div style={{ position:'relative', display:'inline-block', marginBottom: 16 }}>
            <div className="avatar avatar-xl" style={{ margin:'0 auto', width: 96, height: 96, fontSize:'1.6rem', background: avatarPreview ? 'transparent' : bgColor }}>
              {avatarPreview
                ? <img src={avatarPreview} alt="avatar" style={{ width:'100%', height:'100%', objectFit:'cover', borderRadius:'50%' }} onError={() => setAvatarPreview(null)} />
                : getInitials(user?.name)
              }
            </div>
            <button
              onClick={() => fileRef.current.click()}
              style={{ position:'absolute', bottom: 0, right: 0, width: 30, height: 30, borderRadius:'50%', background:'var(--primary)', border:'2px solid white', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
              disabled={uploading}
            >
              {uploading ? <div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> : <MdCameraAlt size={14} color="#fff" />}
            </button>
            <input ref={fileRef} type="file" accept="image/*" style={{ display:'none' }} onChange={handleAvatarChange} />
          </div>
          <div style={{ fontWeight: 800, fontSize:'1.05rem', fontFamily:'Syne, sans-serif' }}>{user?.name}</div>
          <div className="text-muted" style={{ fontSize:'0.82rem', marginTop: 2 }}>{user?.email}</div>
          <span className={`badge ${user?.role==='admin'?'badge-critical':'badge-todo'}`} style={{ marginTop: 10, display:'inline-flex' }}>{user?.role}</span>
          <p style={{ fontSize:'0.75rem', color:'var(--text-light)', marginTop: 14 }}>Click the camera icon to upload a new photo</p>
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap: 20 }}>
          {/* ── Edit Profile ── */}
          <div className="card card-body">
            <h3 style={{ fontWeight: 800, marginBottom: 20, display:'flex', alignItems:'center', gap: 8 }}><MdPerson color="var(--primary)" /> Edit Profile</h3>
            <form onSubmit={handleProfile}>
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input className="form-control" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-control" value={user?.email} disabled style={{ opacity: 0.6, cursor:'not-allowed' }} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Phone (with country code)</label>
                <input
                  className="form-control"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+923001234567"
                />
              </div>
              <button type="submit" className="btn btn-primary" style={{ marginTop: 20 }} disabled={loading}>
                {loading ? 'Saving...' : <><MdSave /> Save Changes</>}
              </button>
            </form>
          </div>

          {/* ── Change Password ── */}
          <div className="card card-body">
            <h3 style={{ fontWeight: 800, marginBottom: 20, display:'flex', alignItems:'center', gap: 8 }}><MdLock color="var(--primary)" /> Change Password</h3>
            <form onSubmit={handlePassword}>
              <div className="form-group">
                <label className="form-label">Current Password</label>
                <input className="form-control" type="password" value={pwForm.currentPassword} onChange={(e) => setPwForm({...pwForm, currentPassword: e.target.value})} required />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">New Password</label>
                <input className="form-control" type="password" value={pwForm.newPassword} onChange={(e) => setPwForm({...pwForm, newPassword: e.target.value})} minLength={6} required />
              </div>
              <button type="submit" className="btn btn-primary" style={{ marginTop: 20 }} disabled={pwLoading}>
                {pwLoading ? 'Updating...' : <><MdLock /> Update Password</>}
              </button>
            </form>
          </div>

          {/* ── Task Submission Link ── */}
          <div className="card card-body">
            <h3 style={{ fontWeight: 800, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
              <MdLink color="var(--primary)" /> Task Submission Link
            </h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 18, lineHeight: 1.5 }}>
              Share this link with anyone so they can submit a task — no login required.
              The submitter can choose who to assign it to from the form.
            </p>
            <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
              <input
                className="form-control"
                readOnly
                value={submitLinkUrl}
                style={{ fontFamily: 'monospace', fontSize: '0.82rem', flex: 1, cursor: 'text' }}
                onFocus={(e) => e.target.select()}
              />
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCopyLink}
                style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <MdContentCopy size={15} /> Copy
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Profile;
